import React, { useState, useEffect } from 'react'
import ReactModal from 'react-modal'
import cn from 'classnames'
import { useQuery, useMutation } from '@apollo/client'
import { toast } from 'react-toastify'
// Components
import Selector from '../../components/formModal/Selector'
import RadioSelector from '../../components/formModal/RadioSelector'
import SimpleInput from '../../components/formModal/SimpleInput'
import Radio from '../newRadio/Radio'
import ToastModal from '../../components/breeder/toastModal'
// Utils
import { stateMap } from '../../utils/states'
import { valEmail, valPhone } from '../apply/lib/utils'
import { GET_ALL_BREEDS_NAMES, CREATE_BREED_NOTIFICATION } from '../../graphql'
// Styles
import styles from './notificationFormModal.module.scss'

const NotificationFormModal = ({
  isOpen,
  close,
  setIsSubmitted,
  landingType,
  breedName,
  breedSlug,
  stateName,
}) => {
  const FORM_INITIAL_STATE = {
    name: '',
    email: '',
    phone: '',
    comment: '',
    gender: '',
    breeds: breedSlug ? [{ value: breedSlug, label: breedSlug }] : [],
    location: stateName ? [{ value: stateName, label: stateName }] : [],
    bringHome: '',
    range: '',
  }
  const [form, setForm] = useState(FORM_INITIAL_STATE)
  const [showError, setShowError] = useState(false)
  const { data } = useQuery(GET_ALL_BREEDS_NAMES)
  const [submit] = useMutation(CREATE_BREED_NOTIFICATION, {
    onCompleted: (data) => {
      if (!data) {
        console.error('Error creating mutation')
      } else {
        setIsSubmitted()
        close()
      }
    },
    onError: (error) => {
      toast(
        <ToastModal
          position="bottom-right"
          theme="error"
          title="Error"
          message={`Unknown error, please try again. Error:${error}`}
          onClick={() => {
            toast.dismiss()
          }}
        />,
      )
    },
  })
  // Form validation
  const isCompleted =
    form.name !== '' &&
    form.email !== '' &&
    valEmail(form.email) &&
    valPhone(form.phone) &&
    form.breeds.length !== 0

  useEffect(() => {
    if (isCompleted) setShowError(false)
  }, [isCompleted])

  if (typeof window !== 'undefined') {
    ReactModal.setAppElement('body')
  }

  // Date Options
  const bringHomeOptions = [
    { value: 'ASAP', label: 'ASAP' },
    { value: 'In the next month', label: 'In the next month' },
    { value: '1 - 3 months', label: '1 - 3 months' },
    { value: '4 - 6 months', label: '4 - 6 months' },
    { value: '6+ Months', label: '6+ Months' },
  ]
  // Gender Options
  const genderOptions = [
    { value: 'Female', label: 'Female' },
    { value: 'Male', label: 'Male' },
  ]

  // Price Range Options
  const priceRangeOptions = [
    { value: 'Up to $1500', label: 'Up to $1500' },
    { value: 'Up to $3000', label: 'Up to $3000' },
    { value: 'Up to $4500', label: 'Up to $4500' },
    { value: 'Over $4500', label: 'Over $4500' },
  ]

  // Location Options
  const locationOptions = []
  for (const state in stateMap) {
    locationOptions.push({
      value: stateMap[state].name,
      label: stateMap[state].name,
    })
  }

  // Breeds QueryOptions
  const breedsOptions = []
  if (data && data?.breeds) {
    data?.breeds.forEach((breed) =>
      breedsOptions.push({
        value: breed?.name,
        label: breed?.name,
        image: breed?.profilePicture?.small,
        slug: breed?.slug,
        id: breed?.id,
      }),
    )
  }
  // Breeds Values
  const selectedBreeds = []
  const selectedStates = []
  if (form.breeds.length !== 0) {
    form.breeds.map((breed) =>
      selectedBreeds.push({
        breedId: breed?.id,
        breedName: breed?.value,
        slug: breed?.slug,
      }),
    )
  }

  useEffect(() => {
    const breed = breedsOptions.find((breed) => breed.value === breedName)
    if (landingType === 'breed' && breed !== undefined) {
      setForm({ ...form, breeds: [breed] })
    } else if (landingType === 'state' && stateName !== undefined) {
      setForm({ ...form, location: [{ value: stateName, label: stateName }] })
    } else if (
      landingType === 'breedState' &&
      breed !== undefined &&
      stateName !== undefined
    ) {
      setForm({
        ...form,
        breeds: [breed],
        location: [{ value: stateName, label: stateName }],
      })
    }
  }, [breedSlug, stateName])

  if (form.location.length !== 0)
    form.location.map((stateName) => selectedStates.push(stateName.value))

  const handleSubmit = () => {
    // Validates not null fields
    if (!isCompleted) {
      return setShowError(true)
    } else {
      submit({
        variables: {
          customerBreedNotifications: {
            name: form?.name,
            email: form?.email,
            phone: form?.phone,
            comments: form?.comment,
            gender: form?.gender?.value,
            bringHome: form?.bringHome?.value,
            range: form?.range?.value,
            isSubscribed: true,
            customerSelectedBreeds: selectedBreeds,
            customerSelectedStates: selectedStates,
          },
        },
      })
    }
  }

  return (
    <ReactModal
      isOpen={isOpen}
      overlayClassName={styles.modalOverlay}
      className={styles.modal}
      onRequestClose={() => close()}
    >
      <div className={styles.closeButton} onClick={() => close()}>
        <img src="/form_modal/close.svg" alt="close icon" />
      </div>
      <div className={styles.title}>
        <h2>What breed are you looking for?</h2>
        <p>Enter the information below to help us find your dream puppy.</p>
      </div>
      <div className={styles.form}>
        <h4>Preferred Breeds</h4>
        <div className={cn(styles.section, styles.multi)}>
          <Selector
            options={breedsOptions}
            placeholder="Search breeds"
            value={form?.breeds}
            onChange={(breeds) => setForm({ ...form, breeds })}
            // Multiple
            isMultiple
            // Filterable
            isFilterable
            errorMessage="Please enter preferred breeds"
            showError={showError && form?.breeds?.length === 0}
            hasImage
          />
        </div>

        <h4>
          Preferred Gender <span>(optional)</span>
        </h4>
        <Radio
          options={genderOptions}
          value={form?.gender?.value}
          onChange={(gender) => setForm({ ...form, gender })}
          inlineBox
        />

        <h4>
          When to Bring Home <span>(optional)</span>
        </h4>
        <RadioSelector
          options={bringHomeOptions}
          placeholder="Select Date"
          theme="radio-rounded"
          value={form.bringHome}
          onChange={(date) => setForm({ ...form, bringHome: date })}
          clearSelection={() => setForm({ ...form, bringHome: '' })}
        />

        <h4>
          State / Province <span>(optional)</span>
        </h4>
        <div className={cn(styles.section, styles.multi)}>
          <Selector
            options={locationOptions}
            placeholder="Search State or Province"
            value={form?.location}
            onChange={(location) => setForm({ ...form, location })}
            // Multiple
            isMultiple
            // Filterable
            isFilterable
          />
        </div>

        <h4>
          Preferred Price <span>(optional)</span>
        </h4>
        <RadioSelector
          options={priceRangeOptions}
          placeholder="Select Price"
          theme="radio-rounded"
          value={form.range}
          onChange={(price) => setForm({ ...form, range: price })}
          clearSelection={() => setForm({ ...form, range: '' })}
        />

        <div className={styles.commentsLabel}>
          <h4>
            Comments <span>(optional)</span>
          </h4>
        </div>
        <textarea
          name="comment"
          placeholder="Comment"
          value={form.comment}
          onChange={(e) => setForm({ ...form, comment: e.target.value })}
        />

        <hr />

        <SimpleInput
          type="text"
          label="Name"
          placeholder="Enter your name"
          value={form?.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          showError={showError && form.name === ''}
        />

        <SimpleInput
          type="email"
          label="email address"
          placeholder="Enter your email"
          value={form?.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          showError={showError && !valEmail(form.email)}
        />

        <SimpleInput
          type="tel"
          label="phone number"
          placeholder="Enter your phone number"
          value={form?.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          showError={showError && !valPhone(form.phone)}
        />

        <button
          onClick={handleSubmit}
          style={!showError && isCompleted ? { background: '#FF1E57' } : null}
        >
          <img src="/form_modal/alert.svg" alt="alert" /> Notify Me
        </button>
      </div>
    </ReactModal>
  )
}

export default NotificationFormModal
